<template>
  <div class="approvals-summary-cards">
    <WorkflowSummaryCardsSkeleton
      v-if="isLoading"
      class="summary-cards-skeleton"
    />
    <template v-else>
      <WorkflowUpToDateCard
        card-title="Approved"
        icon="flsa-check-circle-outline"
        :percentage="approvedPct"
        :label="approvedLabel"
        :selected="isCardSelected(['approved'])"
        @click="handleCardClick(['approved'])"
      />
      <WorkflowSummaryCard
        v-for="card in summaryCards"
        :key="card.key"
        caption="job profiles"
        :icon="card.icon"
        :icon-class="card.iconClass"
        :border-class="card.borderClass"
        :label="card.label"
        :value="card.value"
        :selected="isCardSelected(card.optionIds)"
        :compact="compactCards"
        @click="handleCardClick(card.optionIds)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';
import { useAppBase } from '@/stores/app/base';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useRouter, useRoute } from 'vue-router';
import WorkflowSummaryCardsSkeleton from '@/components/library/shared/WorkflowSummaryCardsSkeleton.vue';
import WorkflowUpToDateCard from '@/components/library/shared/WorkflowUpToDateCard.vue';
import WorkflowSummaryCard from '@/components/library/shared/WorkflowSummaryCard.vue';
import {
  APPROVAL_STATUS_CARD_CONFIG,
  APPROVAL_STATUS_ICONS,
  APPROVAL_STATUS_LABELS,
} from '@/components/library/approvals/helpers';
import { useTracking } from '@/stores/tracking/tracking';

const COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH = 1400;

const props = defineProps({
  totals: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const { selectStatus, updateSelectedStatuses } = useApprovalsDashboardStore();
const { selectedStatuses } = storeToRefs(useApprovalsDashboardStore());
const { windowWidth } = storeToRefs(useAppBase());
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const router = useRouter();
const route = useRoute();

const totalFlows = computed(() => props.totals?.total || 0);
const approvedCount = computed(() => props.totals?.approved || 0);

const approvedPct = computed(() => {
  if (!totalFlows.value) return 0;

  return Math.round((approvedCount.value / totalFlows.value) * 100);
});

const approvedLabel = computed(() => {
  if (!totalFlows.value) return '0%';

  const rounded = Math.round((approvedCount.value / totalFlows.value) * 100);

  if (approvedCount.value > 0 && rounded === 0) return '<1%';

  return `${rounded}%`;
});

const compactCards = computed(() => (
  filterDrawerOpen.value && windowWidth.value < COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH
));

const handleCardClick = (optionIds) => {
  const statusLabel = optionIds.map((id) => APPROVAL_STATUS_LABELS[id] || id).join(', ');

  useTracking().approvalLibSelectedMetricCard({ status: statusLabel });

  selectStatus(optionIds, router);
};

const isCardSelected = (optionIds) => {
  const selected = [...selectedStatuses.value].sort();
  const sorted = [...optionIds].sort();

  return selected.length === sorted.length
    && selected.every((id, i) => id === sorted[i]);
};

const summaryCards = computed(() => {
  const { totals = {} } = props;

  return ['pending', 'rejected', 'cancelled'].map((key) => ({
    key,
    label: APPROVAL_STATUS_LABELS[key],
    value: totals[key] || 0,
    icon: APPROVAL_STATUS_ICONS[key],
    borderClass: APPROVAL_STATUS_CARD_CONFIG[key].borderClass,
    iconClass: APPROVAL_STATUS_CARD_CONFIG[key].iconClass,
    optionIds: [key],
  }));
});

onMounted(() => {
  if (route.query?.status) {
    if (Array.isArray(route.query.status)) {
      updateSelectedStatuses(route.query.status);
    } else {
      updateSelectedStatuses(route.query.status.split(','));
    }
  }
});
</script>

<style lang="less" scoped>
.approvals-summary-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  min-width: 0;
}

.summary-cards-skeleton {
  grid-column: 1 / -1;
  min-width: 0;
}
</style>
